import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setEditableMessage } from '../../actions/chat/actions';
import { CHATBOT_ID } from '../../constants/chatbotId';
import {
  EReplyTemplateTrackingSource,
  trackReplyTemplateCreateModalOpen,
  trackReplyTemplateEditClick,
  trackReplyTemplateItemClick,
  trackReplyTemplateListModalOpen,
} from '../../containers/ReplyTemplateModalContainer/tracking';
import {
  currentChatOpponentIdSelector,
  currentOpenChatSelector,
  hostTypeSelector,
  userSelector,
} from '../../selectors';
import {
  closeModal,
  openModal,
  selectEditableReplyTemplateId,
  selectIsReplyTemplateEnabled,
  selectIsReplyTemplateInputVisible,
  selectIsReplyTemplateNewEnabled,
  selectReplyTemplateErrorMessage,
  selectReplyTemplateInitialText,
  selectReplyTemplateIsModalOpen,
  selectReplyTemplateLimit,
  selectReplyTemplateModalStep,
  selectReplyTemplatesList,
  selectReplyTemplateStatus,
  setEditableReplyTemplateId,
  setIsReplyTemplateNewEnabled,
  setIsReplyTemplateOnboardingEnabled,
  setModalStep,
  setReplyTemplateInitialText,
} from '../../slices/replyTemplatesSlice';
import {
  createUserReplyTemplateThunk,
  deleteUserReplyTemplateThunk,
  getReplyTemplatesThunk,
  updateUserReplyTemplateThunk,
} from '../../thunks/replyTemplates';
import { EAvailableActions } from '../../types/availableActions';
import { EHostType } from '../../types/hostType';
import { EReplyTemplateModalStep } from '../../types/replyTemplates';
import { EAgentAccountType } from '../../types/userData';

const REPLY_TEMPLATES_NEW_KEY = 'isReplyTemplatesOpened';
const REPLY_TEMPLATES_ONBOARDING_KEY = 'isReplyTemplatesOnboardingShown';

export const useReplyTemplateModal = () => {
  const dispatch = useDispatch();

  const isReplyTemplateModalOpen = useSelector(selectReplyTemplateIsModalOpen);
  const isReplyTemplateEnabled = useSelector(selectIsReplyTemplateEnabled);
  const isReplyTemplateNewEnabled = useSelector(selectIsReplyTemplateNewEnabled);
  const isReplyTemplateInputButtonVisible = useSelector(selectIsReplyTemplateInputVisible);

  const replyTemplates = useSelector(selectReplyTemplatesList);
  const replyTemplateModalStep = useSelector(selectReplyTemplateModalStep);
  const replyTemplatesLimit = useSelector(selectReplyTemplateLimit);
  const replyTemplateStatus = useSelector(selectReplyTemplateStatus);
  const replyTemplateErrorMessage = useSelector(selectReplyTemplateErrorMessage);
  const replyTemplateInitialText = useSelector(selectReplyTemplateInitialText);
  const editableReplyTemplateId = useSelector(selectEditableReplyTemplateId);
  const currentChat = useSelector(currentOpenChatSelector);
  const hostType = useSelector(hostTypeSelector);
  const user = useSelector(userSelector);

  const opponentId = useSelector(currentChatOpponentIdSelector);
  const chatId = currentChat?.chatId;
  const availableActions = currentChat?.availableActions;
  const isIframe = hostType === EHostType.Frame;

  React.useEffect(() => {
    const isOnboardingShown = localStorage.getItem(REPLY_TEMPLATES_ONBOARDING_KEY);
    dispatch(setIsReplyTemplateOnboardingEnabled(!isOnboardingShown));
  }, [dispatch]);

  const isReplyTemplateNewAvailable = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return !localStorage.getItem(REPLY_TEMPLATES_NEW_KEY) && isReplyTemplateNewEnabled;
  }, [isReplyTemplateNewEnabled]);

  const isReplyTemplateAvailable = React.useMemo(() => {
    // Если фича-тогл выключен и неуспешный ответ ручки списка шаблонов ответов
    if (!isReplyTemplateEnabled) {
      return false;
    }

    // Если чат с Циан-помощник
    if (chatId === CHATBOT_ID) {
      return false;
    }

    // Если в iframe нет availableActions, пропускаем проверку (новый чат)
    if ((isIframe && typeof availableActions === 'undefined') || (isIframe && availableActions === null)) {
      return true;
    }

    // Если прямой заход в создание шаблона без чата
    if (!chatId) {
      return true;
    }

    // Проверяем возможность отправлять сообщения (существующий чат)
    return Boolean(availableActions?.includes(EAvailableActions.SendMessage));
  }, [isReplyTemplateEnabled, chatId, isIframe, availableActions]);

  // На кнопке вложений ставим точку (все кроме риелторов)
  const isReplyTemplateNewEnabledAttachButton = React.useMemo(() => {
    return (
      isReplyTemplateNewAvailable &&
      user.agentAccountType !== EAgentAccountType.Specialist &&
      user.agentAccountType !== EAgentAccountType.Agency
    );
  }, [isReplyTemplateNewAvailable, user.agentAccountType]);

  // На кнопке с молнией у инпута ставим точку (только для риелторов)
  const isReplyTemplateNewEnabledInputButton = React.useMemo(() => {
    return (
      isReplyTemplateNewAvailable &&
      (user.agentAccountType === EAgentAccountType.Specialist || user.agentAccountType === EAgentAccountType.Agency)
    );
  }, [isReplyTemplateNewAvailable, user.agentAccountType]);

  // В модалке вложений добавляем пункт с лейблом "Новое" (для всех)
  const isReplyTemplateNewEnabledAttachModal = React.useMemo(() => {
    return isReplyTemplateNewAvailable;
  }, [isReplyTemplateNewAvailable]);

  const openReplyTemplateModal = (source: EReplyTemplateTrackingSource) => {
    if (replyTemplates.length === 0) {
      dispatch(setModalStep(EReplyTemplateModalStep.Create));
      trackReplyTemplateCreateModalOpen({ chatId, opponentId, source });
    } else {
      dispatch(setModalStep(EReplyTemplateModalStep.List));
      trackReplyTemplateListModalOpen({ chatId, opponentId, source });
    }

    dispatch(openModal());

    if (isReplyTemplateNewAvailable) {
      dispatch(setIsReplyTemplateNewEnabled(false));
      localStorage.setItem(REPLY_TEMPLATES_NEW_KEY, '1');
    }
  };

  const openReplyTemplateModalEdit = (id: number) => {
    trackReplyTemplateEditClick({ chatId, opponentId, replyTemplateId: id });
    dispatch(setEditableReplyTemplateId(id));
    dispatch(setModalStep(EReplyTemplateModalStep.Edit));
  };

  const openReplyTemplateModalCreate = (source: EReplyTemplateTrackingSource, text?: string | null) => {
    if (text) {
      dispatch(setReplyTemplateInitialText(text));
    }
    dispatch(setModalStep(EReplyTemplateModalStep.Create));
    dispatch(openModal());
    trackReplyTemplateCreateModalOpen({ chatId, opponentId, source });

    if (isReplyTemplateNewAvailable) {
      dispatch(setIsReplyTemplateNewEnabled(false));
      localStorage.setItem(REPLY_TEMPLATES_NEW_KEY, '1');
    }
  };

  const closeReplyTemplateModal = () => {
    dispatch(closeModal());
  };

  const changeModalStep = (step: EReplyTemplateModalStep) => {
    dispatch(setModalStep(step));
  };

  const chooseReplyTemplate = (id: number) => {
    const template = replyTemplates.find(template => template.id === id);
    const chatId = currentChat?.chatId;

    if (template && chatId) {
      dispatch(
        setEditableMessage({
          chatId: currentChat?.chatId,
          text: replyTemplates.find(template => template.id === id)?.text || '',
        }),
      );
      trackReplyTemplateItemClick({ chatId, opponentId, replyTemplateId: id });
    }
    dispatch(closeModal());
  };

  const createReplyTemplate = (text: string, name?: string) => {
    dispatch(createUserReplyTemplateThunk({ name, text }));

    dispatch(setIsReplyTemplateOnboardingEnabled(false));
    localStorage.setItem(REPLY_TEMPLATES_ONBOARDING_KEY, '1');
  };

  const editReplyTemplate = (id: number, text: string, name?: string) => {
    dispatch(updateUserReplyTemplateThunk({ id, text, name }));
  };

  const deleteReplyTemplate = (id: number) => {
    dispatch(deleteUserReplyTemplateThunk({ id }));
  };

  const getReplyTemplates = () => {
    dispatch(getReplyTemplatesThunk());
  };

  const editableReplyTemplate = React.useMemo(() => {
    return replyTemplates.find(template => template.id === editableReplyTemplateId) || null;
  }, [editableReplyTemplateId, replyTemplates]);

  return {
    replyTemplates,
    replyTemplatesLimit,
    isReplyTemplateModalOpen,
    isReplyTemplateEnabled: isReplyTemplateAvailable,
    isReplyTemplateNewEnabledAttachButton,
    isReplyTemplateNewEnabledInputButton,
    isReplyTemplateNewEnabledAttachModal,
    isReplyTemplateInputButtonVisible,
    replyTemplateModalStep,
    editableReplyTemplate,
    replyTemplateStatus,
    replyTemplateErrorMessage,
    replyTemplateInitialText,
    openReplyTemplateModal,
    openReplyTemplateModalEdit,
    openReplyTemplateModalCreate,
    closeReplyTemplateModal,
    changeModalStep,
    createReplyTemplate,
    editReplyTemplate,
    deleteReplyTemplate,
    getReplyTemplates,
    chooseReplyTemplate,
  };
};
